import gql from 'graphql-tag';
import { useMemo } from 'react';

import type {
  AdActivityGQLResponse,
  AdGroupActivityGQLResponse,
  AdGroupsGQLResponse,
  AdsGQLResponse,
} from '@/api/ad/adType';
import { getVariablesFromTableParamsNew } from '@/api/util/getVariablesFromTableParams';
import { InstantSearchSortOrder, type SolQueryParamsNew } from '@/components/InstantSearch';
import { useSolInfiniteQuery, useSolQuery } from '@/hooks/useSolQuery';

const adGroupActivityQuery = gql`
  query AdGroupActivity(
    $page: Pagination!
    $sort: [SortParamInput]!
    $filter: [FilterParamInput]!
    $startDate: DateTime!
    $endDate: DateTime!
    $audienceId: String!
  ) {
    visualization {
      adGroupActivity(
        page: $page
        sort: $sort
        filter: $filter
        startDate: $startDate
        endDate: $endDate
        audienceId: $audienceId
      ) {
        edges {
          node {
            adGroup {
              id
              name
              campaign {
                id
                name
                vendor {
                  id
                  name
                  channel {
                    id
                    name
                  }
                }
              }
            }
            adClicks
            adImpressions
            allVisits: targetVisits
            allVisitsBest: visitsBest
            allVisitsWorst: visitsWorst
            baseAccounts
            botVisits
            closedWonFirstTouchAttribution
            closedWonInfluenced
            closedWonLinearAttribution
            companiesReached
            countCampaigns
            countClosedwonInfluenced
            countPipelineInfluenced
            customImpactScore
            engagedCompanies
            engagedCompaniesBest
            engagedCompaniesWorst
            fitScore
            fitScoreBest
            fitScoreWorst
            leadsInfluenced
            opportunitiesInfluenced
            opportunityInfluencedBest
            opportunityInfluencedSumClosedWonCustom
            opportunityInfluencedSumPipelineCustom
            opportunityInfluencedWorst
            otherVisits
            percentOfEngagedCompanies
            percentOpportunitiesInfluenced
            pipelineFirstTouchAttribution
            pipelineInfluenced
            pipelineInfluencedBest
            pipelineInfluencedPerSpend
            pipelineInfluencedWorst
            pipelineLastTouchAttribution
            pipelineLinearAttribution
            spend
            spendPerTargetVisit
            spendPerVisitBest
            spendPerVisitWorst
            botViewThroughVisits
            otherViewThroughVisits
            targetViewThroughVisits
            targetVisits
            unresolvedViewThroughVisits
            unresolvedVisits
            viewThroughPercent
            viewThroughVisits
            visitEfficiency
          }
        }
        totalEdges
      }
    }
  }
`;

const adActivityQuery = gql`
  query AdActivity(
    $page: Pagination!
    $sort: [SortParamInput]!
    $filter: [FilterParamInput]!
    $startDate: DateTime!
    $endDate: DateTime!
    $audienceId: String!
  ) {
    visualization {
      adActivity(
        page: $page
        sort: $sort
        filter: $filter
        startDate: $startDate
        endDate: $endDate
        audienceId: $audienceId
      ) {
        edges {
          node {
            ad {
              id
              name
              matchType
              adGroup {
                id
                name
                campaign {
                  id
                  name
                  vendor {
                    id
                    name
                    channel {
                      id
                      name
                    }
                  }
                }
              }
              detail {
                metadata
              }
            }
            adClicks
            adImpressions
            allVisits: targetVisits
            allVisitsBest: visitsBest
            allVisitsWorst: visitsWorst
            baseAccounts
            botVisits
            closedWonFirstTouchAttribution
            closedWonInfluenced
            closedWonLinearAttribution
            companiesReached
            countCampaigns
            countClosedwonInfluenced
            countPipelineInfluenced
            customImpactScore
            engagedCompanies
            engagedCompaniesBest
            engagedCompaniesWorst
            fitScore
            fitScoreBest
            fitScoreWorst
            leadsInfluenced
            opportunitiesInfluenced
            opportunityInfluencedBest
            opportunityInfluencedSumClosedWonCustom
            opportunityInfluencedSumPipelineCustom
            opportunityInfluencedWorst
            otherVisits
            percentOfEngagedCompanies
            percentOpportunitiesInfluenced
            pipelineFirstTouchAttribution
            pipelineInfluenced
            pipelineInfluencedBest
            pipelineInfluencedPerSpend
            pipelineInfluencedWorst
            pipelineLastTouchAttribution
            pipelineLinearAttribution
            spend
            spendPerTargetVisit
            spendPerVisitBest
            spendPerVisitWorst
            botViewThroughVisits
            otherViewThroughVisits
            targetViewThroughVisits
            targetVisits
            unresolvedViewThroughVisits
            unresolvedVisits
            viewThroughPercent
            viewThroughVisits
            visitEfficiency
          }
        }
        totalEdges
      }
    }
  }
`;

export const useAdGroupActivity = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audienceId?: string,
  filters: { channelIds?: string[] } = {},
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;

  const tableParamVars = getVariablesFromTableParamsNew(tableParams);

  const { data, isLoading, error } = useSolQuery<AdGroupActivityGQLResponse>(
    {
      query: !!canExecuteQuery && adGroupActivityQuery,
      variables: {
        ...tableParamVars,
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
        filter: (tableParamVars?.filter ?? []).concat(
          filters.channelIds
            ? [
                {
                  field: 'channel.id',
                  operator: 'in',
                  operand: filters.channelIds,
                },
              ]
            : [],
        ),
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    adGroupActivity: data?.visualization.adGroupActivity.edges.map(({ node }) => node),
    totalResults: data?.visualization.adGroupActivity.totalEdges,
    isLoading,
    error,
  };
};

export const useAdGroupActivityFitScore = (
  adGroupIds: string[],
  startDate?: string,
  endDate?: string,
  audienceId?: string,
) => {
  const canExecuteQuery = adGroupIds?.length && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<AdGroupActivityGQLResponse>(
    {
      query: !!canExecuteQuery && adGroupActivityQuery,
      variables: {
        page: {
          offset: 0,
          limit: 50,
        },
        sort: [
          {
            field: 'fitScore',
            direction: 'desc',
          },
        ],
        filter: [
          {
            field: 'id',
            operator: 'in',
            operand: adGroupIds,
          },
        ],
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    fitScoreData: data?.visualization.adGroupActivity.edges.map(({ node }) => node),
    isLoading,
    error,
  };
};

export const useAdActivity = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audienceId?: string,
  filters: { channelIds?: string[] } = {},
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;
  const tableParamVars = getVariablesFromTableParamsNew(tableParams);
  const { data, isLoading, error } = useSolQuery<AdActivityGQLResponse>(
    {
      query: !!canExecuteQuery && adActivityQuery,
      variables: {
        ...tableParamVars,
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
        filter: (tableParamVars?.filter ?? []).concat(
          filters.channelIds
            ? [
                {
                  field: 'channel.id',
                  operator: 'in',
                  operand: filters.channelIds,
                },
              ]
            : [],
        ),
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    adActivity: data?.visualization.adActivity.edges.map(({ node }) => node),
    totalResults: data?.visualization.adActivity.totalEdges,
    isLoading,
    error,
  };
};

export const useAdActivityFitScore = (
  adIds: string[],
  startDate?: string,
  endDate?: string,
  audienceId?: string,
) => {
  const canExecuteQuery = adIds?.length && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<AdActivityGQLResponse>(
    {
      query: !!canExecuteQuery && adActivityQuery,
      variables: {
        page: {
          offset: 0,
          limit: 50,
        },
        sort: [
          {
            field: 'fitScore',
            direction: 'desc',
          },
        ],
        filter: [
          {
            field: 'adId',
            operator: 'in',
            operand: adIds,
          },
        ],
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    fitScoreData: data?.visualization.adActivity.edges.map(({ node }) => node),
    isLoading,
    error,
  };
};

export const useAds = ({
  search = '',
  pageSize = 50,
  channelId,
}: {
  search?: string;
  pageSize?: number;
  channelId?: string;
}) => {
  const { data, isLoading, isFetchingNextPage, error, fetchNextPage } =
    useSolInfiniteQuery<AdsGQLResponse>({
      queryKey: ['ads', search, channelId],
      queryFn: ({ pageParam = 0, signal }) => {
        return {
          query: gql`
            query GetAds(
              $page: Pagination!
              $sort: [SortParamInput]!
              $filter: [FilterParamInput]!
            ) {
              ads {
                get(page: $page, sort: $sort, filter: $filter) {
                  edges {
                    node {
                      adGroup {
                        id
                        name
                        campaign {
                          id
                          name
                          vendor {
                            id
                            name
                            color
                            channel {
                              color
                              id
                              name
                            }
                          }
                        }
                      }
                      id
                      name
                      detail {
                        id
                        metadata
                      }
                    }
                  }
                  pageMeta {
                    hasNext
                  }
                }
              }
            }
          `,
          variables: {
            ...getVariablesFromTableParamsNew({
              page: {
                offset: pageParam * pageSize,
                limit: pageSize,
              },
              sort: {
                direction: InstantSearchSortOrder.ASC,
                field: 'name',
              },
              filter: [
                ...(search
                  ? [{ field: 'name', operator: 'iLike' as const, operand: `%${search}%` }]
                  : []),
                ...(channelId
                  ? [{ field: 'channelId', operator: 'eq' as const, operand: channelId }]
                  : []),
              ],
            }),
          },
          signal,
        };
      },
      getNextPageParam: (lastPage, pages) => {
        return lastPage.ads.get.pageMeta.hasNext ? pages.length + 1 : undefined;
      },
    });

  const hasMore = data?.pages?.[data.pages.length - 1].ads.get.pageMeta.hasNext;

  const adOptions = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.pages.map((d) => d.ads.get.edges.map((edge) => edge.node)).flat();
  }, [data]);

  return {
    adOptions,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
  };
};

export const useAdGroups = ({
  search = '',
  pageSize = 50,
  channelId,
}: {
  search?: string;
  pageSize?: number;
  channelId?: string;
}) => {
  const { data, isLoading, isFetchingNextPage, error, fetchNextPage } =
    useSolInfiniteQuery<AdGroupsGQLResponse>({
      queryKey: ['adGroups', search, channelId],
      queryFn: ({ pageParam = 0, signal }) => {
        return {
          query: gql`
            query GetAdGroups(
              $page: Pagination!
              $sort: [SortParamInput]!
              $filter: [FilterParamInput]!
            ) {
              adGroups {
                get(page: $page, sort: $sort, filter: $filter) {
                  edges {
                    node {
                      id
                      name
                      campaign {
                        id
                        name
                        vendor {
                          id
                          name
                          color
                          channel {
                            color
                            id
                            name
                          }
                        }
                      }
                    }
                  }
                  pageMeta {
                    hasNext
                  }
                }
              }
            }
          `,
          variables: {
            ...getVariablesFromTableParamsNew({
              page: {
                offset: pageParam * pageSize,
                limit: pageSize,
              },
              sort: {
                direction: InstantSearchSortOrder.ASC,
                field: 'name',
              },
              filter: [
                ...(search
                  ? [{ field: 'name', operator: 'iLike' as const, operand: `%${search}%` }]
                  : []),
                ...(channelId
                  ? [{ field: 'channelId', operator: 'eq' as const, operand: channelId }]
                  : []),
              ],
            }),
          },
          signal,
        };
      },
      getNextPageParam: (lastPage, pages) => {
        return lastPage.adGroups.get.pageMeta.hasNext ? pages.length + 1 : undefined;
      },
    });

  const hasMore = data?.pages?.[data.pages.length - 1].adGroups.get.pageMeta.hasNext;

  const adGroupOptions = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.pages.map((d) => d.adGroups.get.edges.map((edge) => edge.node)).flat();
  }, [data]);

  return {
    adGroupOptions,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
  };
};
